import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid2,
  Typography,
  Avatar,
  Button,
  Divider,
} from "@mui/material";
import moment from "moment";

import "../RecentActivities/RecentActivity.css";
import {
  RecentActivities,
  RecentInsights,
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import {
  GetRecentActivities,
  GetRecentInsights,
} from "../../ApiServices/ApiRequests";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import {
  MasterDataContextType,
  useMasterData,
} from "../../ContextAPI/MasterContext";

const RecentCard: React.FC<{
  title: string;
  items: RecentActivities[] | RecentInsights[] | undefined;
}> = ({ title, items = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const toggleShowAll = (): void => setShowAll((prev) => !prev);
  const displayedItems: RecentActivities[] | RecentInsights[] = showAll
    ? items
    : items?.slice(0, 1) || [];
  const { t } = useTranslation();

  return (
    <Card variant="outlined" sx={{ p: 2, marginTop: "20px", height: "220px" }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        {title}
      </Typography>
      <Divider />
      {items.length === 0 ? (
        // Show 'No data available' message when items are empty

        <Typography
          variant="body2"
          color="textSecondary"
          textAlign="center"
          mt={5}
        >
          {t("global.Nodataavailable")}
        </Typography>
      ) : (
        <>
          <Box
            className="scrollable-section"
            sx={{
              maxHeight: showAll ? 90 : "none",
              overflowY: showAll ? "auto" : "visible",
              transition: "max-height 0.3s ease",
            }}
          >
            {displayedItems.map((item, index) => (
              <div key={index}>
                <Box sx={{ display: "flex", alignItems: "flex-start", my: 2 }}>
                  <Avatar sx={{ bgcolor: "orange", mr: 2 }}>
                    {(item?.groupName?.split(" ")[0]?.[0] || "") +
                      (item?.groupName?.split(" ")[1]?.[0] || "")}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <span>{item?.groupName}</span>
                      <div>
                        <Typography
                          noWrap
                          variant="body2"
                          color="textSecondary"
                        >
                          {moment(item?.dateTime).isSame(moment(), "day")
                            ? "Today"
                            : moment(item?.dateTime).format("ll")}
                        </Typography>
                      </div>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item?.deviceName}
                    </Typography>
                    <Typography variant="body2">{item?.message}</Typography>
                  </Box>
                </Box>
                <Divider />
              </div>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              variant="body2"
              color="#8850EF"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={toggleShowAll}
            >
              {showAll ? "Hide all" : "Show all"}
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
};

// Main component to display Recent Insights and Recent Activities with Google Map

const RecentItems: React.FC = () => {
  const { masterState, dispatchMaster } = useMasterData();

  const handleInputChange = useCallback(
    (
      field: keyof MasterDataContextType,
      value:
        | string
        | boolean
        | RecentActivities[]
        | RecentInsights[]
        | number
        | null
    ) => {
      dispatchMaster({ type: "SET_FIELD", field, value });
    },
    [dispatchMaster]
  );

  useEffect(() => {
    if (masterState.selectedSiteCode.length > 0) {
      fetchRecentActivities(masterState.selectedSiteCode);
      fetchRecentInsights(masterState.selectedSiteCode);
    }
  }, [masterState.selectedSiteCode]);

  const fetchRecentActivities = async (siteCode: string) => {
    try {
      const data = await GetRecentActivities(siteCode);
      handleInputChange("recentActivities", data);
    } catch (error) {}
  };

  const fetchRecentInsights = async (siteCode: string) => {
    try {
      const data = await GetRecentInsights(siteCode);
      handleInputChange("recentInsights", data);
    } catch (error) {
      console.error("error loading master site  recentinsights");
    }
  };

  return (
    <Box>
      <Grid2 container spacing={0} direction="column">
        <Grid2 size={{ xs: 12, md: 12 }}>
          <RecentCard
            title={t("global.RecentInsights")}
            items={masterState.recentInsights}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 12 }} sx={{ mt: 2 }}>
          <RecentCard
            title={t("global.RecentActivities")}
            items={masterState.recentActivities}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default RecentItems;
